import React from 'react';
import { Button } from './button';
import { ArrowUpDown, ArrowUp, ArrowDown, Filter } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './dropdown-menu';

// Sortable Table Header Component
export function SortableTableHead({ 
  children, 
  sortKey, 
  currentSort, 
  onSort, 
  filterOptions,
  currentFilter,
  onFilter,
  className = ''
}) {
  const isActive = currentSort?.key === sortKey;
  const direction = isActive ? currentSort?.direction : null;

  const handleSort = () => {
    if (!onSort) return;
    
    let newDirection = 'asc';
    if (isActive && direction === 'asc') {
      newDirection = 'desc';
    } else if (isActive && direction === 'desc') {
      newDirection = null;
    }
    
    onSort(newDirection ? { key: sortKey, direction: newDirection } : null);
  };

  const SortIcon = direction === 'asc' ? ArrowUp : direction === 'desc' ? ArrowDown : ArrowUpDown;

  return (
    <th className={`text-muted-foreground font-medium text-left p-4 ${className}`}>
      <div className="flex items-center gap-1">
        {onSort ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSort}
            className={`-ml-3 h-8 text-muted-foreground hover:text-foreground ${isActive ? 'text-foreground' : ''}`}
          >
            {children}
            <SortIcon className={`ml-1 h-4 w-4 ${isActive ? 'text-emerald-400' : ''}`} />
          </Button>
        ) : (
          <span>{children}</span>
        )}
        
        {filterOptions && filterOptions.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className={`h-6 w-6 p-0 ${currentFilter ? 'text-emerald-400' : 'text-muted-foreground'}`}
              >
                <Filter className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem 
                onClick={() => onFilter && onFilter(null)}
                className={!currentFilter ? 'bg-white/10' : ''}
              >
                All
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {filterOptions.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => onFilter && onFilter(option.value)}
                  className={currentFilter === option.value ? 'bg-white/10' : ''}
                >
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </th>
  );
}

// Helper function to sort data
export function sortData(data, sort) {
  if (!sort || !sort.key) return data;
  
  return [...data].sort((a, b) => {
    let aVal = a[sort.key];
    let bVal = b[sort.key];
    
    // Handle nested keys like 'user.name'
    if (sort.key.includes('.')) {
      const keys = sort.key.split('.');
      aVal = keys.reduce((obj, key) => obj?.[key], a);
      bVal = keys.reduce((obj, key) => obj?.[key], b);
    }
    
    // Handle null/undefined
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return sort.direction === 'asc' ? 1 : -1;
    if (bVal == null) return sort.direction === 'asc' ? -1 : 1;
    
    // Handle dates
    if (aVal instanceof Date || (typeof aVal === 'string' && !isNaN(Date.parse(aVal)))) {
      aVal = new Date(aVal).getTime();
      bVal = new Date(bVal).getTime();
    }
    
    // Handle numbers
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sort.direction === 'asc' ? aVal - bVal : bVal - aVal;
    }
    
    // Handle strings
    const aStr = String(aVal).toLowerCase();
    const bStr = String(bVal).toLowerCase();
    
    if (sort.direction === 'asc') {
      return aStr.localeCompare(bStr);
    }
    return bStr.localeCompare(aStr);
  });
}

// Helper function to filter data
export function filterData(data, filters) {
  if (!filters || Object.keys(filters).length === 0) return data;
  
  return data.filter(item => {
    return Object.entries(filters).every(([key, value]) => {
      if (value == null || value === '') return true;
      
      let itemVal = item[key];
      
      // Handle nested keys
      if (key.includes('.')) {
        const keys = key.split('.');
        itemVal = keys.reduce((obj, k) => obj?.[k], item);
      }
      
      return itemVal === value;
    });
  });
}

export default SortableTableHead;
